import React from 'react';
import { useDispatch } from 'react-redux';
import Aux from '../../../hoc/Auxilary/Auxilary';
import Snackbar from '@mui/material/Snackbar';
import AlertTitle from '@mui/material/AlertTitle';
import MuiAlert from '@mui/material/Alert';
import Slide from '@mui/material/Slide';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

// Alert payloads come from hundreds of call sites, and some hand the raw axios
// error or a serialized validation object straight through. React cannot render
// those, and an unrenderable child tears the whole app down through the error
// boundary - so anything that is not already a valid node becomes text here.
function alertText(value) {
  if (value == null || typeof value === 'string' || React.isValidElement(value)) return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map((v) => alertText(v)).join(' ');
  if (value instanceof Error) return value.message;
  if (typeof value === 'object') {
    if (typeof value.message === 'string') return value.message;
    if (typeof value.error === 'string') return value.error;
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
}

// Build a plain-text version of an alert for the clipboard.
function alertCopyText(title, message) {
  return [alertText(title), alertText(message)]
    .filter((t) => typeof t === 'string' && t.trim())
    .join(' — ');
}

// Action cluster shown on error alerts: copy-to-clipboard + close, so users
// can grab the exact message to send to support.
function AlertActions({ text, onClose }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — ignore */ }
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Tooltip title={copied ? 'Copied' : 'Copy message'}>
        <IconButton size="small" color="inherit" onClick={copy} aria-label="Copy message">
          {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <IconButton size="small" color="inherit" onClick={onClose} aria-label="Dismiss">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert ref={ref} {...props}
                   sx={{ mb: props.type === 'modal' ? 8 : null,
                         width: { xs: '75vw', md: '400px' }
                        }}/>;
});

export function LayoutToaster(props) {
  const { message, title, severity, isOpen } = props.alert;
  const dispatch = useDispatch();

  return(
    <Aux>
      <Snackbar 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center'}} 
        autoHideDuration={5000}
        disableWindowBlurListener={true}
        key={`${severity}-alert`}
        onClose={() => dispatch({ type: 'CLEAR_ALERT' })}
        open={isOpen}
        TransitionComponent={SlideTransition}
      >
        <Alert severity={severity} type='layout'
          onClose={() => dispatch({ type: 'CLEAR_ALERT' })}
          action={severity === 'error'
            ? <AlertActions text={alertCopyText(title, message)} onClose={() => dispatch({ type: 'CLEAR_ALERT' })} />
            : undefined}>
          <AlertTitle>{alertText(title)}</AlertTitle>
          {alertText(message)}
        </Alert>
      </Snackbar>
    </Aux>
  )
}

export function ModalToaster(props) {
  if(props.alert == null || props.alert === '')
    return null;
  const { message, onClose, title, severity } = props.alert;

  const isOpen = props?.alert != null && props?.alert !== '' ? true : false;

  return(
    <Aux>
      <Snackbar 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center'}} 
        autoHideDuration={5000}
        disableWindowBlurListener={true}
        key={`${severity}-alert`}
        onClose={() => onClose()}
        open={isOpen}
        TransitionComponent={SlideTransition}
      >
        <Alert severity={severity} type='modal'
          onClose={() => onClose()}
          action={severity === 'error'
            ? <AlertActions text={alertCopyText(title, message)} onClose={() => onClose()} />
            : undefined}>
          <AlertTitle>{alertText(title)}</AlertTitle>
          {alertText(message)}
        </Alert>
      </Snackbar>
    </Aux>
  )
}